// ============================================================
// EXPENSE FORECAST ENGINE
//
// Specialized pipeline for forecasting all business expenses.
//
// Categorization:
// 1. MARKETING: Projects platform spend (Meta, Google, etc.
//    Supports manual target-spend sliders.
// 2. JOURNALS: Discrete manual/recurring operational costs.
// 3. SPECS: Calculated costs (taxes, fees) scaled by revenue.
// 4. GENERAL: Everything else (stochastic/historical trends).
// ============================================================


import {
    ExpenseForecastResult,
    ExpenseSourceForecast,
    ExpensePoint,
    RoasForecastResult,
    NonPaidForecastResult,
    JournalOperation
} from './types';
import { BoardJournal, BoardSpec, BoardEntity } from '../../../../../stores/boardStore';
import { projectJournalOperations, buildDailyContext } from './combinationEngine';
import { evaluate } from '../../../../../lib/formulaParser';
import { SOURCE_COLORS, MARKETING_PLATFORMS } from './constants';
import { normalRandom } from './math';
import { computeAdaptiveJitter } from './adaptiveJitter';

/**
 * Main Expense Forecast Engine.
 * 
 * Pipeline:
 * Stage 1: Historical Extraction & Mapping
 * Stage 2: Discrete Projections (Marketing, Journals, Specs)
 * Stage 3: Aggregation & Combination
 * 
 * @param finance           The finance data object
 * @param forecastDays      Number of days to forecast
 * @param marketingWeights  Per-platform manual spend targets (from sliders)
 * @param journals          Manual journals data
 * @param journalOperations Fetched operations for journals
 * @param specs             Calculated specs
 * @param roasPoints        PREVIOUSLY CALCULATED revenue forecasts (needed for Specs)
 * @param nonPaidPoints     PREVIOUSLY CALCULATED revenue forecasts (needed for Specs)
 */
export const computeExpenseForecast = (
    finance: any,
    _forecastDays: number,
    marketingWeights: Record<string, number>,
    journals: BoardJournal[],
    journalOperations: JournalOperation[],
    specs: BoardSpec[],
    roasData: RoasForecastResult,
    nonPaidData: NonPaidForecastResult,
    _entities?: BoardEntity[],
    _channels?: any[]
): ExpenseForecastResult => {
    const txs = finance?.transactions?.filter((tx: any) => tx.isActive !== false) || [];

    if (!roasData.combinedChart || roasData.combinedChart.length === 0) {
        return { sources: [], combinedChart: [], totalHistorical: 0, totalForecasted: 0 };
    }
    const templateDates = roasData.combinedChart.map(pt => pt.date || pt.label);
    const forecastStartIdx = roasData.combinedChart.findIndex(pt => pt.actualSpend === null && !pt.actualRevenue);

    // ============================================================
    // STAGE 1: MARKETING SPEND (Seasonality + Jitter)
    // ============================================================
    const marketingSources: ExpenseSourceForecast[] = roasData.perSourceCharts.map(res => {
        const platform = res.source; // Ad/Entity Name
        const entityId = res.id;
        const color = SOURCE_COLORS[res.platform] || SOURCE_COLORS.default;

        // 1a. Day-of-week seasonality for marketing spend
        const recentDates = templateDates.slice(Math.max(0, forecastStartIdx - 28), forecastStartIdx);
        const dowSpend = new Array(7).fill(0), dowCount = new Array(7).fill(0);

        recentDates.forEach(date => {
            const pt = res.data.find(h => h.date === date);
            if (pt && pt.actualSpend !== null) {
                const dow = new Date(date).getDay();
                dowSpend[dow] += pt.actualSpend;
                dowCount[dow]++;
            }
        });

        const recentMean = (dowSpend.reduce((a, b) => a + b, 0) / Math.max(1, dowCount.reduce((a, b) => a + b, 0))) || 1;
        const dowMult = new Array(7).fill(1);
        for (let d = 0; d < 7; d++) {
            if (dowCount[d] > 0) dowMult[d] = (dowSpend[d] / dowCount[d]) / recentMean;
        }

        // 1b. Adaptive Jitter for spend
        const spendValues = res.data.filter(pt => !pt.actualRoas && pt.actualSpend !== null).map(pt => pt.actualSpend || 0);
        const spendJitter = computeAdaptiveJitter(spendValues, spendValues.filter(v => v > 0), templateDates.length, 0, undefined, true);

        const data: ExpensePoint[] = templateDates.map((date, i) => {
            const isForecast = i >= forecastStartIdx;
            const historicalPt = res.data.find(h => h.date === date);
            const actual = (!isForecast && historicalPt) ? historicalPt.actualSpend : null;

            if (!isForecast) {
                return {
                    label: roasData.combinedChart[i].label,
                    date,
                    actual,
                    forecast: i === forecastStartIdx - 1 ? actual : null,
                    upper: null,
                    lower: null,
                    isForecast
                };
            }

            const sliderVal = marketingWeights[entityId];
            const baseVal = sliderVal !== undefined ? sliderVal : res.avgSpend;

            // Apply Seasonality (Damped)
            const dow = new Date(date).getDay();
            const seasonDamping = Math.exp(-(i - forecastStartIdx) * 0.05);
            const seasonalBase = baseVal * (1 + (dowMult[dow] - 1) * seasonDamping);

            // Apply Jitter
            const noise = normalRandom(`${platform}-spend-${i}`) * seasonalBase * spendJitter.relativeCV * 0.5; // halved for spend
            const margin = seasonalBase * spendJitter.relativeCV * Math.sqrt(1 + 0.1 * (i - forecastStartIdx)) * 0.8;

            return {
                label: roasData.combinedChart[i].label,
                date,
                actual: null,
                forecast: Math.max(0, seasonalBase + noise),
                upper: Math.max(0, seasonalBase + margin),
                lower: Math.max(0, seasonalBase - margin),
                isForecast
            };
        });

        return {
            sourceId: `marketing-${platform}`,
            sourceName: `${platform.charAt(0).toUpperCase() + platform.slice(1)} Ad Spend`,
            type: 'marketing',
            color,
            data
        };
    });

    // ============================================================
    // STAGE 2: JOURNALS (Discrete Inputs)
    // ============================================================
    const journalSources: ExpenseSourceForecast[] = journals.filter(j => j.isEnabled).map(journal => {
        const ops = journalOperations.filter(op => op.journalId === journal.id);
        const projection = projectJournalOperations([journal], ops, templateDates);

        const historicalValue = new Array(templateDates.length).fill(0);
        const dateToIndex = new Map(templateDates.map((d, i) => [d, i]));

        txs.forEach((t: any) => {
            if (String(t.entityId) === `journal-${journal.id}` || String(t.entityId) === journal.id) {
                const dt = t.date?.split('T')[0];
                const idx = dateToIndex.get(dt);
                if (idx !== undefined && idx < forecastStartIdx) {
                    const amt = Number(t.convertedAmount || t.amount || 0);
                    historicalValue[idx] += amt;
                }
            }
        });

        const data: ExpensePoint[] = templateDates.map((date, i) => {
            const isForecast = i >= forecastStartIdx;
            const val = projection[i];

            return {
                label: roasData.combinedChart[i].label,
                date,
                actual: !isForecast ? historicalValue[i] : null,
                forecast: isForecast ? val : (i === forecastStartIdx - 1 ? historicalValue[i] : null),
                upper: isForecast ? val : null,
                lower: isForecast ? val : null,
                isForecast
            };
        });

        return {
            sourceId: `journal-${journal.id}`,
            sourceName: journal.name,
            type: 'journal',
            color: '#94a3b8',
            data
        };
    });

    // ============================================================
    // STAGE 3: SPECS (Calculated + Historical Context)
    // ============================================================
    const dailySpendMap: Record<string, number[]> = {};
    marketingSources.forEach(ms => {
        const pk = ms.sourceName.split(' ')[0].toLowerCase();
        dailySpendMap[pk] = ms.data.map(pt => pt.actual !== null ? pt.actual : (pt.forecast || 0));
    });

    const { rev } = buildDailyContext(roasData, nonPaidData, templateDates);
    const journalAdjustments = projectJournalOperations(journals, journalOperations, templateDates);
    const journalProjExpenses = journalAdjustments.map((v: number) => Math.abs(Math.min(0, v)));

    // Extract actual historical expenses per day for Stage 3 validation
    const historicalTotalExpenses = new Array(templateDates.length).fill(0);
    const dateToIndex_S3 = new Map(templateDates.map((d, i) => [d, i]));
    txs.forEach((t: any) => {
        const dt = t.date?.split('T')[0];
        const idx = dateToIndex_S3.get(dt);
        if (idx !== undefined && idx < forecastStartIdx) {
            const amt = Number(t.convertedAmount || t.amount || 0);
            if (t.type === 'EXPENSE' || (t.type === 'REVENUE' && amt < 0)) {
                historicalTotalExpenses[idx] += Math.abs(amt);
            }
        }
    });

    const specSources: ExpenseSourceForecast[] = specs.map(spec => {
        const data: ExpensePoint[] = templateDates.map((date, i) => {
            const isForecast = i >= forecastStartIdx;

            const context: Record<string, number> = {
                'all.revenues': 0,
                'all.expenses': isForecast ? journalProjExpenses[i] : (historicalTotalExpenses[i] || 0),
            };

            Object.keys(rev).forEach(src => {
                const r = rev[src][i];
                const s = dailySpendMap[src] ? dailySpendMap[src][i] : 0;
                context[`${src}.revenues`] = r;
                context[`${src}.expenses`] = s;
                context['all.revenues'] += r;
                if (isForecast) context['all.expenses'] += s;
            });

            const val = evaluate(spec.formula, context);
            const expenseVal = val;

            return {
                label: roasData.combinedChart[i].label,
                date,
                actual: !isForecast ? expenseVal : null,
                forecast: isForecast ? expenseVal : (i === forecastStartIdx - 1 ? expenseVal : null),
                upper: isForecast ? expenseVal : null,
                lower: isForecast ? expenseVal : null,
                isForecast
            };
        });

        return {
            sourceId: `spec-${spec.id}`,
            sourceName: spec.name,
            type: 'spec',
            color: '#6366f1',
            data
        };
    });

    // ============================================================
    // STAGE 4: GENERAL EXPENSES (Historical Trend + Jitter)
    // ============================================================
    const generalHistorical = new Array(templateDates.length).fill(0);
    const dateToIndex_G = new Map(templateDates.map((d, i) => [d, i]));

    txs.forEach((t: any) => {
        if (t.type !== 'EXPENSE') return;
        const dt = t.date?.split('T')[0];
        const idx = dateToIndex_G.get(dt);
        if (idx === undefined || idx >= forecastStartIdx) return;

        const traffic = t.traffic ? (typeof t.traffic === 'string' ? JSON.parse(t.traffic) : t.traffic) : {};
        const platform = t.platform?.toLowerCase() || traffic.target?.toLowerCase() || '';
        const isMarketing = MARKETING_PLATFORMS.some(mp => platform.includes(mp));
        if (isMarketing) return;

        const isJournal = String(t.entityId).startsWith('journal-') || journals.some(j => j.id === t.entityId);
        if (isJournal) return;

        generalHistorical[idx] += Math.abs(Number(t.amount || 0));
    });

    const recentGeneral = generalHistorical.slice(Math.max(0, forecastStartIdx - 28), forecastStartIdx);
    const genJitter = computeAdaptiveJitter(recentGeneral, recentGeneral.filter(v => v > 0), 28, 0, undefined, true);
    const avgGeneral = recentGeneral.length > 0 ? recentGeneral.reduce((a, b) => a + b, 0) / recentGeneral.length : 0;

    const generalSource: ExpenseSourceForecast = {
        sourceId: 'general',
        sourceName: 'Operational & Other',
        type: 'general',
        color: '#f43f5e',
        data: templateDates.map((date, i) => {
            const isForecast = i >= forecastStartIdx;
            if (!isForecast) {
                return {
                    label: roasData.combinedChart[i].label,
                    date,
                    actual: generalHistorical[i],
                    forecast: i === forecastStartIdx - 1 ? generalHistorical[i] : null,
                    upper: null,
                    lower: null,
                    isForecast
                };
            }

            const noise = normalRandom(`general-exp-${i}`) * avgGeneral * genJitter.relativeCV * 0.7;
            const margin = avgGeneral * genJitter.relativeCV * Math.sqrt(1 + 0.15 * (i - forecastStartIdx));

            return {
                label: roasData.combinedChart[i].label,
                date,
                actual: null,
                forecast: Math.max(0, avgGeneral + noise),
                upper: Math.max(0, avgGeneral + margin),
                lower: Math.max(0, avgGeneral - margin),
                isForecast
            };
        })
    };

    // ============================================================
    // FINAL: AGGREGATION
    // ============================================================
    const allSources = [...marketingSources, ...journalSources, ...specSources, generalSource];
    const combinedChart: ExpensePoint[] = templateDates.map((date, i) => {
        const isForecast = i >= forecastStartIdx;
        let tActual = 0, tForecast = 0, tUpper = 0, tLower = 0, hasActual = false;

        allSources.forEach(s => {
            const pt = s.data[i];
            if (pt.actual !== null) { tActual += pt.actual; hasActual = true; }
            if (isForecast) {
                tForecast += pt.forecast || 0;
                tUpper += pt.upper || (pt.forecast || 0);
                tLower += pt.lower || (pt.forecast || 0);
            }
        });

        return {
            label: roasData.combinedChart[i].label,
            date,
            actual: hasActual ? tActual : null,
            forecast: isForecast ? tForecast : (i === forecastStartIdx - 1 ? tActual : null),
            upper: isForecast ? tUpper : null,
            lower: isForecast ? tLower : null,
            isForecast
        };
    });

    const totalHistorical = combinedChart.filter(p => !p.isForecast).reduce((s, p) => s + (p.actual || 0), 0);
    const totalForecasted = combinedChart.filter(p => p.isForecast).reduce((s, p) => s + (p.forecast || 0), 0);

    return {
        sources: allSources,
        combinedChart,
        totalHistorical,
        totalForecasted
    };
};
